import { useEffect, useMemo, useRef, useState } from 'react';
import { useFetcher } from 'react-router';
import { useTracker } from '@archipro-website/tracker';
import { useUserLoggedIn } from '@modules/user';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import {
    GateHeading,
    GateSubcopy,
} from '~/modules-rocco/user/component/auth-modal/GateHeading';
import { GateStickyFooter } from '~/modules-rocco/user/component/auth-modal/GateStickyFooter';
import {
    BOOKING_AVAILABILITY_API_ROUTE,
    type BookingAvailabilityApiResponse,
} from '~/routes/remix-api.booking.availability';
import { BOOKING_BOOK_API_ROUTE } from '~/routes/remix-api.booking.book';
import {
    BOOKING_REQUEST_AVAILABILITY_API_ROUTE,
    type BookingRequestAvailabilityApiResponse,
} from '~/routes/remix-api.booking.request-availability';
import { BOOKING_REQUEST_API_ROUTE } from '~/routes/remix-api.booking.request';
import type { BookingRosterMember } from '../api/get-request-availability.server';
import { useBookingSchedule } from '../hooks/use-booking-schedule';
import { toCompanyTierAvailability } from '../utils/build-request-availability';
import type { BookMeetingGateIntent } from '../book-meeting-gate-state';
import { BookMeetingGateSteps } from './BookMeetingGateSteps';

dayjs.extend(utc);
dayjs.extend(timezone);

/** Stable empty list, so the seeding effect is not re-run by a new array each render. */
const EMPTY_ROSTER: BookingRosterMember[] = [];

function getBrowserTimezone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
        return 'Pacific/Auckland';
    }
}

export interface BookMeetingSubject {
    subjectName: string;
    professionalName: string;
    /** The person's own photo, when the professional has one public. */
    image?: string;
    companyLogo?: string | null;
    logoBackgroundColor?: string | null;
    /** The day and time asked for, formatted for the success step. */
    when?: string;
    /** No public team member, so the subject is the practice itself. */
    isCompany: boolean;
    /** A connected calendar, so the time is booked rather than requested. */
    isBooked: boolean;
}

interface BookMeetingGateBodyProps {
    intent: BookMeetingGateIntent;
    /**
     * Reports who the reader is meeting, so the shell can draw them on the left
     * pane. Availability is loaded here, so this is the only place that knows.
     */
    onSubject?: (subject: BookMeetingSubject) => void;
    /** The request landed, so the shell can show its success step and close. */
    onSent?: () => void;
    /**
     * Ask the host before sending. Returns false when it needs to stand the
     * auth wall up first, in which case it sends on the far side instead.
     */
    onBeforeSend: () => boolean;
    /** Bumped by the host once auth resolves, so the chosen slot sends itself. */
    submitSignal: number;
    /** Bumped when the reader backs out of the wall, disarming that send. */
    cancelSignal?: number;
    /**
     * The route refused to send as the current session — none, or one naming a
     * different account than this page shows. The host stands the auth wall
     * back up; the chosen slot is already armed here, so resolving the wall
     * re-sends it as whoever just signed in.
     */
    onNeedsLogin?: () => void;
}

/**
 * Availability and the submit lifecycle for the booking gate.
 *
 * Split from the modal shell so the shell only decides which of
 * [choose] / [auth] / [sent] is on screen, and from the step components so they
 * only draw. See BC-5174.
 */
export const BookMeetingGateBody = ({
    intent,
    onSubject,
    onSent,
    onBeforeSend,
    submitSignal,
    cancelSignal = 0,
    onNeedsLogin,
}: BookMeetingGateBodyProps) => {
    const { isLoggedIn, user } = useUserLoggedIn();
    /**
     * A project links one specific session, which may be a round-robin with its
     * own member list, so those pages still open straight onto it. Everywhere
     * else the professional's roster decides, per person. See BC-5188.
     */
    const pinnedSessionId = intent.sessionIds?.[0];
    const serverTimezone = getBrowserTimezone();

    // The working week and the roster in one quick call — no calendar provider
    // is touched, so this returns long before real availability would.
    const rosterFetcher = useFetcher<BookingRequestAvailabilityApiResponse>();
    const rosterLoadedRef = useRef<string | null>(null);
    useEffect(() => {
        if (pinnedSessionId) return;
        if (rosterLoadedRef.current === intent.professionalUrlSegment) return;
        rosterLoadedRef.current = intent.professionalUrlSegment;
        rosterFetcher.load(
            `${BOOKING_REQUEST_AVAILABILITY_API_ROUTE}?professional=${encodeURIComponent(intent.professionalUrlSegment)}`,
        );
        // rosterFetcher is deliberately omitted: its own state changes must not
        // re-trigger the load and cancel the request in flight.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pinnedSessionId, intent.professionalUrlSegment]);

    /**
     * Kept from the last load that carried one, because the fetcher empties
     * while it revalidates — sending re-runs it, and the roster blinking out
     * mid-send took the chosen person with it: the pane fell back to the
     * practice and the picker vanished under the reader's cursor.
     */
    const [lastRoster, setLastRoster] =
        useState<BookingRosterMember[]>(EMPTY_ROSTER);
    const fetchedRoster = rosterFetcher.data?.roster;
    useEffect(() => {
        if (fetchedRoster?.length) setLastRoster(fetchedRoster);
    }, [fetchedRoster]);

    /**
     * Read from the fetcher first, so the render that receives the roster
     * already has it. Held only in state, it arrived an effect late: that paint
     * had availability but no roster, so it drew the practice with no picker
     * and then swapped a chosen member's calendar in behind it.
     */
    const roster = fetchedRoster?.length ? fetchedRoster : lastRoster;
    const requestAvailability = rosterFetcher.data?.availability;
    /** A request needs an enquiry to land in. Absent until the roster loads. */
    const allowsEnquiries = rosterFetcher.data?.allowsEnquiries ?? true;

    /**
     * The CTA named someone the roster does not have — a stale id, or a member
     * unpublished since the page rendered. Deliberately not conditioned on the
     * flow being a request: when it was, a bookable first member turned the
     * check off and the gate opened on that stranger's calendar.
     */
    const preferredIsUnknown =
        Boolean(intent.preferredTeamMemberId) &&
        roster.length > 0 &&
        !roster.some(
            (member) => member.teamMemberId === intent.preferredTeamMemberId,
        );

    /**
     * Who the gate opens on: the person the CTA named, and nobody at all when
     * it named none. Derived rather than seeded in an effect so the first paint
     * after the roster lands is already right.
     *
     * A CTA that named no one is asking the practice, which is what the profile
     * header and the project pages do. Falling back to the first of the roster
     * put a stranger's name and face on the pane a beat after it had correctly
     * shown the practice, and quietly made the request about them. An
     * unrecognised name resolves to nobody for the same reason.
     */
    const defaultTeamMemberId = useMemo(() => {
        if (!intent.preferredTeamMemberId || preferredIsUnknown) return null;
        return (
            roster.find(
                (member) =>
                    member.teamMemberId === intent.preferredTeamMemberId,
            )?.teamMemberId ?? null
        );
    }, [roster, intent.preferredTeamMemberId, preferredIsUnknown]);

    /** The reader's own pick, which stands once made. */
    const [chosenTeamMemberId, setChosenTeamMemberId] = useState<number | null>(
        null,
    );
    const hasChosenRef = useRef(false);
    const setSelectedTeamMemberId = (next: number | null) => {
        hasChosenRef.current = true;
        setChosenTeamMemberId(next);
    };
    const selectedTeamMemberId = hasChosenRef.current
        ? chosenTeamMemberId
        : defaultTeamMemberId;

    const selectedMember =
        roster.find((member) => member.teamMemberId === selectedTeamMemberId) ??
        null;

    /**
     * Real availability, fetched only once the reader picks someone whose
     * calendar is connected. A practice with no integrations never calls it.
     */
    const sessionId = pinnedSessionId ?? selectedMember?.sessionId ?? null;
    const sessionFetcher = useFetcher<BookingAvailabilityApiResponse>();
    const sessionLoadedRef = useRef<number | null>(null);
    useEffect(() => {
        if (!sessionId || sessionLoadedRef.current === sessionId) return;
        sessionLoadedRef.current = sessionId;
        sessionFetcher.load(
            `${BOOKING_AVAILABILITY_API_ROUTE}?sessionId=${sessionId}&timezone=${encodeURIComponent(serverTimezone)}&singleTimezone=1`,
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId, serverTimezone]);

    /** A connected calendar books outright; everyone else takes a request. */
    const isRequest = !sessionId;
    const loaded = isRequest
        ? requestAvailability
        : sessionFetcher.data?.availability;
    /**
     * A request with nowhere to land. The roster keeps its bookable people when
     * enquiries are off, so this is only reached by falling back to the
     * practice: no member chosen, or the CTA named one the roster lost.
     */
    const requestWithNoEnquiry = isRequest && !allowsEnquiries;

    /**
     * Demand with nowhere to land is still demand. The dead end is otherwise
     * invisible — nothing is written anywhere — so it emits its own event.
     * `allowsEnquiries` defaults on until the roster answers, so this cannot
     * fire before the professional's real setting is known.
     */
    const tracker = useTracker();
    const deadEndTrackedRef = useRef(false);
    useEffect(() => {
        if (!requestWithNoEnquiry || deadEndTrackedRef.current) return;
        deadEndTrackedRef.current = true;
        const payload = {
            authSource: 'bookMeeting',
            professional: intent.professionalUrlSegment,
        };
        void tracker.log('BookMeetingDeadEnd', {
            url: new URL(window.location.href),
            data: {
                ...payload,
                Source: payload.authSource,
                ExtraData: JSON.stringify(payload),
            },
        });
    }, [requestWithNoEnquiry, tracker, intent.professionalUrlSegment]);
    const loadError =
        (isRequest ? rosterFetcher.data?.error : sessionFetcher.data?.error) ??
        (requestWithNoEnquiry
            ? {
                  code: 'enquiriesClosed' as const,
                  message:
                      'This team is not taking meeting requests online. Send them an enquiry instead.',
              }
            : undefined);
    const isLoading = isRequest
        ? rosterFetcher.state !== 'idle' || (!loaded && !loadError)
        : sessionFetcher.state !== 'idle' || (!loaded && !loadError);

    const availability = useMemo(() => {
        if (!loaded) return loaded;
        // Nothing to offer: the working week was built before the roster was
        // narrowed, and showing it would take a time that cannot be sent.
        if (requestWithNoEnquiry) return undefined;
        return preferredIsUnknown ? toCompanyTierAvailability(loaded) : loaded;
    }, [loaded, preferredIsUnknown, requestWithNoEnquiry]);

    const booking = useBookingSchedule({
        availability,
        serverTimezone,
        actionPath: isRequest
            ? BOOKING_REQUEST_API_ROUTE
            : BOOKING_BOOK_API_ROUTE,
        // The picker is normally the roster's: it lists everyone the practice
        // shows, across both ways of making a meeting. A pinned session has no
        // roster, so there the payload's own picker is the only one there is,
        // and leaving it off took the choice off every round-robin project.
        enableMemberSelection: Boolean(pinnedSessionId),
        extraFormFields: isRequest
            ? {
                  professionalUrlSegment: intent.professionalUrlSegment,
                  siteTreeId: String(intent.siteTreeId),
                  ...(intent.itemTitle ? { itemTitle: intent.itemTitle } : {}),
                  // The TeamMember row, so the route can name a display-only
                  // person who has no member account to send.
                  ...(selectedTeamMemberId && !preferredIsUnknown
                      ? { teamMemberId: String(selectedTeamMemberId) }
                      : {}),
              }
            : undefined,
    });

    // The member record is who the request is attributed to, but the identity
    // this page is showing travels with it, so the route can refuse when the
    // cookies have moved to another account behind the page. Empty when the
    // page shows nobody — a send straight off the auth wall can run before the
    // loader data catches up, and there the wall itself just said who they are.
    const send = () =>
        booking.handleSubmit({
            clientName: isLoggedIn
                ? [user.FirstName, user.LastName]
                      .filter(Boolean)
                      .join(' ')
                      .trim()
                : '',
            clientEmail: isLoggedIn ? (user.Email ?? '') : '',
            clientCompany: '',
        });

    const [pendingSend, setPendingSend] = useState(false);
    const handleSend = () => {
        if (onBeforeSend()) {
            send();
            return;
        }
        setPendingSend(true);
    };

    // A CTA that named a person opens on that person. Seeded once: the hook
    // reseeds its own default when availability lands, and re-applying this on
    // every render would fight the reader's own choice in the picker.
    const seededMemberRef = useRef(false);
    useEffect(() => {
        if (seededMemberRef.current) return;
        if (!intent.preferredTeamMemberId) return;
        if (!booking.memberOptions.length) return;
        // The CTA names a TeamMember row; availability is keyed on the member
        // account behind it. Both are already in this payload, so the mapping
        // happens here rather than on the page that raised the CTA.
        const memberId = availability?.teamMembers.find(
            (member) => member.ID === intent.preferredTeamMemberId,
        )?.MemberID;
        if (!memberId) return;
        const offered = booking.memberOptions.some(
            (option) => Number(option.value) === memberId,
        );
        if (!offered) return;
        seededMemberRef.current = true;
        booking.setSelectedMemberId(memberId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [intent.preferredTeamMemberId, booking.memberOptions, availability]);

    const professionalName =
        booking.professional?.title ?? intent.professionalName ?? 'the team';

    /**
     * A pinned session is the project pages' path: they open straight onto one
     * linked session and never load the roster, so the people on it are only
     * ever named by the payload. Read the roster there and every project
     * booking called itself a meeting with the practice.
     */
    const sessionSubjectName =
        booking.selectedMember?.name || booking.consultedMember?.name || null;

    /**
     * Who the reader is meeting. Off the roster where there is one, because the
     * payload only knows the people on one side of the choice, and off the
     * payload for a pinned session, which has no roster to read.
     */
    const meetingSubjectName = pinnedSessionId
        ? sessionSubjectName
        : preferredIsUnknown || !selectedMember
          ? null
          : selectedMember.name;
    const isCompany = meetingSubjectName === null;
    const subjectName = meetingSubjectName ?? professionalName;
    const meetingSubject = pinnedSessionId ? null : selectedMember;

    /**
     * The picker's rows and its selection, which come from a different place on
     * each path. Off the roster there is one row per person the practice shows,
     * keyed by TeamMember row. A pinned session has only the payload, whose
     * members are keyed by account id and whose choice belongs to the booking
     * hook, since that is what assigns the round-robin.
     */
    const sessionRoster = useMemo<BookingRosterMember[]>(
        () =>
            booking.memberOptions.map((option) => ({
                teamMemberId: Number(option.value),
                name: option.label,
                // A session payload lists its members by name alone, so the
                // picker's second line is empty on this path.
                jobTitle: '',
                image: null,
                sessionId: pinnedSessionId ?? null,
            })),
        [booking.memberOptions, pinnedSessionId],
    );
    const pickerRoster = pinnedSessionId
        ? sessionRoster
        : preferredIsUnknown
          ? EMPTY_ROSTER
          : roster;
    const pickerSelectedId = pinnedSessionId
        ? booking.selectedMemberId
        : selectedTeamMemberId;
    const onPickMember = pinnedSessionId
        ? booking.setSelectedMemberId
        : setSelectedTeamMemberId;

    const lastCancelRef = useRef(0);
    useEffect(() => {
        if (cancelSignal === 0 || cancelSignal === lastCancelRef.current)
            return;
        lastCancelRef.current = cancelSignal;
        setPendingSend(false);
    }, [cancelSignal]);

    const lastSignalRef = useRef(0);
    useEffect(() => {
        if (submitSignal === 0 || submitSignal === lastSignalRef.current)
            return;
        lastSignalRef.current = submitSignal;
        if (pendingSend) {
            setPendingSend(false);
            send();
        }
        // `send` closes over the current slot, which is what the reader chose.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitSignal, pendingSend]);

    // A send the route refused over identity arms itself again and hands the
    // surface to the wall, so signing in is the only step between the reader
    // and the slot they already chose. Waits for idle so a re-send in flight
    // is not re-armed off its predecessor's stale answer.
    useEffect(() => {
        if (!booking.needsLogin || booking.submissionState !== 'idle') return;
        setPendingSend(true);
        onNeedsLogin?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [booking.needsLogin, booking.submissionState]);

    useEffect(() => {
        if (!availability) return;
        onSubject?.({
            subjectName,
            professionalName,
            isCompany,
            image:
                meetingSubject?.image ??
                booking.selectedMember?.avatarUrl ??
                booking.consultedMember?.avatarUrl ??
                undefined,
            companyLogo: booking.professional?.companyLogo,
            logoBackgroundColor: booking.professional?.logoBackgroundColor,
            isBooked: !isRequest,
            when: booking.selectedSlotTimestamp
                ? dayjs(booking.selectedSlotTimestamp)
                      .tz(booking.timezoneName)
                      .format('dddd, D MMMM YYYY [at] h:mm A')
                : undefined,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        availability,
        subjectName,
        professionalName,
        booking.selectedMember,
        booking.selectedSlotTimestamp,
        isRequest,
        meetingSubject,
    ]);

    useEffect(() => {
        if (booking.success) onSent?.();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [booking.success]);

    // Only a real failure takes the step off screen. While the fetch is in
    // flight the step draws itself, with the month grid as the one part
    // waiting — see BookMeetingGateSteps.
    if (loadError && !availability) {
        return (
            <div className="mx-auto my-auto flex h-full w-full max-w-90.75 flex-col">
                <GateHeading className="mt-3">
                    Not taking meetings yet
                </GateHeading>
                <GateSubcopy>
                    {loadError?.message ??
                        'This team is not taking meeting requests online. Send them an enquiry instead.'}
                </GateSubcopy>
                <div className="flex-1" />
            </div>
        );
    }

    return (
        <>
            <BookMeetingGateSteps
                booking={booking}
                subjectName={subjectName}
                professionalName={professionalName}
                isCompany={isCompany}
                onSend={handleSend}
                isSending={booking.submissionState !== 'idle'}
                isLoading={isLoading}
                isBooked={!isRequest}
                expectsTeamPicker={intent.expectsTeamPicker ?? false}
                roster={pickerRoster}
                selectedTeamMemberId={pickerSelectedId}
                onSelectTeamMember={onPickMember}
                companyLogo={
                    booking.professional?.companyLogo ?? intent.companyLogo
                }
                logoBackgroundColor={intent.logoBackgroundColor}
            />
            {booking.failure ? (
                <GateStickyFooter className="mt-2">
                    <p
                        role="alert"
                        className="text-[0.8125rem] text-destructive"
                    >
                        {booking.statusMessage ??
                            'We could not send your request just now.'}
                    </p>
                </GateStickyFooter>
            ) : null}
        </>
    );
};
